import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Lock, Wrench } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Overline, Loading, ProgressBar, LockCard } from "@/components/shared";

export default function BuildSystem() {
  const { entitlements } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/build-system").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;

  return (
    <div data-testid="build-system-page">
      <Overline>Build Your System</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2">Your minimum viable management system</h1>
      <p className="text-lg text-nm-neutral font-light mt-4 max-w-2xl">Each building block pairs a chapter with a template. Complete the chapters and your operating system assembles itself — one practice at a time.</p>

      {!data.unlocked ? (
        <div className="mt-8"><LockCard part="part_1" teaser="Build Your System draws on Part 1's chapters and templates. Unlock Part 1 to start assembling your operating system." /></div>
      ) : (
        <>
          <div className="mt-8 rounded-3xl border border-nm-primary/12 bg-nm-surface p-8 nm-shadow-sm">
            <div className="flex items-end justify-between gap-4">
              <div>
                <Overline>System completeness</Overline>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-serif text-5xl text-nm-primary">{data.pct}%</span>
                  <span className="text-sm text-nm-neutral">· {data.done} of {data.total} blocks in place</span>
                </div>
              </div>
              <Wrench size={40} className="text-nm-primary/20 hidden sm:block" />
            </div>
            <div className="mt-5"><ProgressBar pct={data.pct} /></div>
          </div>

          {data.recommendations.length > 0 && (
            <div className="mt-8 rounded-3xl border border-nm-highlight/30 bg-gradient-to-b from-[#FCF6E4] to-[#FFFDF8] p-7">
              <div className="text-nm-ink font-medium">Recommended next steps</div>
              <p className="text-sm text-nm-neutral mt-1">Based on what you haven't completed yet.</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                {data.recommendations.map((b) => (
                  <Link key={b.key} to={`/app/chapter/${b.chapter}`} data-testid={`rec-${b.key}`}
                    className="group rounded-2xl bg-white/70 border border-nm-primary/10 p-4 hover:bg-white transition-colors duration-200">
                    <div className="font-serif text-lg text-nm-ink leading-snug">{b.title}</div>
                    <div className="flex items-center gap-1.5 text-sm text-nm-primary mt-3 group-hover:gap-2.5 transition-[gap] duration-200">Start <ArrowRight size={14} /></div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 space-y-3">
            {data.blocks.map((b) => (
              <div key={b.key} className="flex items-center gap-4 rounded-2xl border border-nm-primary/10 bg-nm-surface p-5 nm-shadow-sm" data-testid={`block-${b.key}`}>
                {b.done ? <CheckCircle2 size={22} className="text-nm-green shrink-0" /> : <Circle size={22} className="text-nm-primary/25 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-nm-ink">{b.title}</div>
                  <div className="text-sm text-nm-neutral mt-0.5">Template: {b.template_title}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/app/template/${b.template}`} className="hidden sm:inline-flex text-sm text-nm-neutral hover:text-nm-primary transition-colors">Template</Link>
                  <Link to={`/app/chapter/${b.chapter}`} data-testid={`block-chapter-${b.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-4 py-2 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors duration-200">
                    {b.done ? "Review" : "Open"} <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
